/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TrmUserMedicalFacilityProps {
  id: string;
  trmUserId: string;
  medicalFacilityId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TrmUserMedicalFacilityRecord extends Record<TrmUserMedicalFacilityProps>({
  id: null as any,
  trmUserId: null as any,
  medicalFacilityId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    trmUserId: string;
    medicalFacilityId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TrmUserMedicalFacilityRecord {
    if (json == null) {
      throw new Error('TrmUserMedicalFacilityRecord.fromJS: parameter cannot be null');
    }
    return new TrmUserMedicalFacilityRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TrmUserMedicalFacilityJson.id',
      ),
      trmUserId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'trmUserId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserMedicalFacilityJson.trmUserId',
      ),
      medicalFacilityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserMedicalFacilityJson.medicalFacilityId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserMedicalFacilityJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    trmUserId: string;
    medicalFacilityId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TrmUserMedicalFacilityRecord, Error[]] {
    if (json == null) {
      throw new Error('TrmUserMedicalFacilityRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TrmUserMedicalFacilityRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TrmUserMedicalFacilityJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      trmUserId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'trmUserId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserMedicalFacilityJson.trmUserId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserMedicalFacilityJson.medicalFacilityId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserMedicalFacilityJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TrmUserMedicalFacilityProps) {
    super(props);
    if (props == null) {
      throw new Error('TrmUserMedicalFacilityRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TrmUserMedicalFacilityViewModelBase implements TrmUserMedicalFacilityProps {
  protected readonly _props: TrmUserMedicalFacilityProps;
  private _json?: TrmUserMedicalFacilityProps;

  get id(): string {
    return this._props.id;
  }
  get trmUserId(): string {
    return this._props.trmUserId;
  }
  get medicalFacilityId(): string {
    return this._props.medicalFacilityId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: TrmUserMedicalFacilityProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        trmUserId: this._props.trmUserId,
        medicalFacilityId: this._props.medicalFacilityId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTrmUserMedicalFacilityEntities = (state: AppState) =>
  state.entityStores.trmUserMedicalFacility.entities;

export const selectTrmUserMedicalFacilityById = (id: string) => (state: AppState) =>
  state.entityStores.trmUserMedicalFacility.entities.get(id);

export const selectTrmUserMedicalFacilityMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.trmUserMedicalFacility.meta.get(id);

export const selectTrmUserMedicalFacilityAndMetadataById = (id: string) =>
  createSelector(
    selectTrmUserMedicalFacilityById(id),
    selectTrmUserMedicalFacilityMetadataById(id),
    (trmUserMedicalFacility, meta) => ({ trmUserMedicalFacility, meta }),
  );

export function selectTrmUserMedicalFacilityEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTrmUserMedicalFacilityEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TrmUserMedicalFacilityRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTrmUserMedicalFacilityEntitiesByTrmUserId(trmUserId: string) {
  return createSelector(selectAllTrmUserMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.trmUserId === trmUserId).valueSeq(),
  );
}

export function selectTrmUserMedicalFacilityEntitiesByMedicalFacilityId(medicalFacilityId: string) {
  return createSelector(selectAllTrmUserMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityId === medicalFacilityId).valueSeq(),
  );
}

export function selectTrmUserMedicalFacilityEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTrmUserMedicalFacilityEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
